/**
 * 시공사례 입력 다듬기.
 *
 * 저장소가 아니라 여기 있는 이유 — **어디에 저장하든 같은 규칙**이다. 인메모리와
 * Supabase 가 각자 들고 있으면 한쪽만 고쳤을 때 목록 카드가 조용히 비어 뜬다.
 */

import {
  Portfolio,
  PORTFOLIO_EXCERPT_LINE_MAX,
  PORTFOLIO_EXCERPTS_MAX,
  PORTFOLIO_IMAGE_STAGES,
  PORTFOLIO_PUBLIC_FIELDS,
  PORTFOLIO_PUBLIC_FIELDS_DEFAULT,
  PortfolioImageInput,
  PortfolioPublicField,
  PortfolioTimelineEntry,
} from "./types";

const SPACE_MAX = 30;

/** url 이 빈 장은 버리고, 앞뒤 공백을 턴다. 태그(stage·space)는 알아보는 값만 남긴다. */
export const portfolioImages = (images: PortfolioImageInput[]): Portfolio["images"] =>
  images
    .filter((i) => i.url.trim() !== "")
    .map((i) => {
      const space = i.space?.trim().slice(0, SPACE_MAX);
      return {
        url: i.url.trim(),
        caption: i.caption?.trim() ?? "",
        ...(i.photo_id?.trim() ? { photo_id: i.photo_id.trim() } : {}),
        ...(i.stage && (PORTFOLIO_IMAGE_STAGES as readonly string[]).includes(i.stage) ? { stage: i.stage } : {}),
        ...(space ? { space } : {}),
      };
    });

// 공간 태그 — 그 조직 `codes.space` 에 있는 코드만 남긴다(액션이 조직 코드를 읽어 넘긴다).
export const portfolioImageSpaces = (images: PortfolioImageInput[], known: ReadonlySet<string>): PortfolioImageInput[] =>
  images.map(({ space, ...rest }) => {
    const code = space?.trim();
    return code && known.has(code) ? { ...rest, space: code } : rest;
  });

// 발췌 줄 정리 — 줄 안 공백을 접고 빈 줄은 버린다(폼·스키마·저장이 같이 쓴다).
export const excerptLinesOf = (lines: readonly string[]): string[] =>
  lines.map((l) => l.replace(/\s+/g, " ").trim()).filter(Boolean);

// 발췌 상한 검사 — 정리된 줄을 받아 어긴 규칙의 문구를 돌려준다(폼과 서버 스키마가 같은 문구를 낸다).
export const excerptLineIssues = (kept: readonly string[]): string[] => [
  ...(kept.length > PORTFOLIO_EXCERPTS_MAX ? [`발췌는 챕터마다 ${PORTFOLIO_EXCERPTS_MAX}줄까지 적을 수 있습니다`] : []),
  ...(kept.some((l) => Array.from(l).length > PORTFOLIO_EXCERPT_LINE_MAX)
    ? [`발췌 한 줄은 ${PORTFOLIO_EXCERPT_LINE_MAX}자 이내로 적어 주세요`]
    : []),
];

// 발췌 줄 — 정리한 뒤 줄 길이·줄 수 상한까지 자른다.
export const timelineExcerptLines = (lines: readonly string[]): string[] =>
  excerptLinesOf(lines)
    .map((l) => Array.from(l).slice(0, PORTFOLIO_EXCERPT_LINE_MAX).join(""))
    .slice(0, PORTFOLIO_EXCERPTS_MAX);

export const TIMELINE_MISMATCH_MESSAGE = "공정 챕터가 바뀌었습니다. 새로고침한 뒤 다시 고쳐 주세요";

export class TimelineMismatchError extends Error {
  constructor() {
    super(TIMELINE_MISMATCH_MESSAGE);
    this.name = "TimelineMismatchError";
  }
}

// 챕터별 발췌만 바꾼다 — 이름·날짜·photo_ids 는 저장된 값 그대로, 챕터 수가 다르면 거절.
export const mergeTimelineExcerpts = (
  current: readonly PortfolioTimelineEntry[],
  excerpts: readonly (readonly string[])[]
): PortfolioTimelineEntry[] => {
  if (excerpts.length !== current.length) throw new TimelineMismatchError();
  return current.map((entry, i) => ({
    name: entry.name,
    start: entry.start,
    end: entry.end,
    excerpts: timelineExcerptLines(excerpts[i]),
    photo_ids: [...entry.photo_ids],
  }));
};

// 공개 항목 — 모르는 키·중복을 버리고 정해진 순서로. 안 보냈으면 기본값(기간·면적).
export const portfolioPublicFields = (fields: readonly string[] | undefined): PortfolioPublicField[] => {
  if (fields === undefined) return [...PORTFOLIO_PUBLIC_FIELDS_DEFAULT];
  const picked = new Set(fields);
  return PORTFOLIO_PUBLIC_FIELDS.filter((f) => picked.has(f));
};

// 금액 — 0 이상 안전 정수만, 그 밖은 null.
export const portfolioAmount = (won: number | null | undefined): number | null =>
  typeof won === "number" && Number.isSafeInteger(won) && won >= 0 ? won : null;

/** 빈 태그는 버린다. 공백만 친 태그가 목록에 서면 안 된다. */
export const portfolioTags = (tags: readonly string[]): string[] =>
  tags.map((t) => t.trim()).filter(Boolean);

/**
 * 대표 이미지. **안 고르면 첫 장이 대표다.**
 *
 * 목록 카드가 비어 보이는 쪽이 더 나쁘다 — 사진을 올려 놓고도 갤러리에 회색 칸이
 * 뜬다.
 */
export const portfolioCover = (
  cover: string | undefined,
  images: Portfolio["images"]
): string => cover?.trim() || (images[0]?.url ?? "");
